/* Printable production instructions. Original artwork remains a separate order file. */
(() => {
  'use strict';
  if (window.stEngravingJobSheet) return;
  const W = 1224, H = 1584, M = 76;
  const ink = '#17221b', muted = '#536157', green = '#376b29';

  function text(ctx, value, x, y, width, size = 26, bold = false) {
    ctx.font = `${bold ? '700' : '400'} ${size}px Arial, sans-serif`;
    ctx.fillStyle = ink;
    ctx.textBaseline = 'top';
    const lines = [];
    for (const paragraph of String(value || '').split(/\r?\n/)) {
      let line = '';
      // Break long identifiers as well as normal text; never clip customer notes.
      for (const char of paragraph) {
        if (line && ctx.measureText(line + char).width > width) {
          const space = line.lastIndexOf(' ');
          if (space > line.length / 2) {
            lines.push(line.slice(0, space));
            line = line.slice(space + 1) + char;
          } else {
            lines.push(line);
            line = char;
          }
        } else line += char;
      }
      lines.push(line);
    }
    for (const line of lines) { ctx.fillText(line, x, y); y += size * 1.35; }
    return y;
  }

  function page(title, reference, number, total) {
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw Error('Could not create the engraving job sheet.');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = green; ctx.fillRect(M, M, 10, 74);
    text(ctx, 'STATEN TOOLS / ENGRAVING', M + 30, M, W - M * 2 - 30, 21, true);
    text(ctx, title, M + 30, M + 34, W - M * 2 - 30, 34, true);
    text(ctx, reference, M, H - 99, W - M * 2, 19);
    text(ctx, 'Production reference • Preview images are not to scale', M, H - 70, W - M * 2 - 180, 18);
    text(ctx, `${number} / ${total}`, W - M - 100, H - 70, 100, 18);
    return {canvas, ctx};
  }

  function row(ctx, label, value, y) {
    text(ctx, label.toUpperCase(), M, y + 3, 225, 19, true);
    const end = text(ctx, value, M + 248, y, W - M * 2 - 248, 25);
    ctx.strokeStyle = '#dfe5df'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(M, end + 12); ctx.lineTo(W - M, end + 12); ctx.stroke();
    return end + 28;
  }

  async function image(ctx, file, box) {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve; img.onerror = () => reject(Error('A job sheet preview could not be loaded. Please try again.'));
        img.src = url;
      });
      const scale = Math.min(box.w / img.naturalWidth, box.h / img.naturalHeight);
      const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
      ctx.drawImage(img, box.x + (box.w - w) / 2, box.y + (box.h - h) / 2, w, h);
    } finally { URL.revokeObjectURL(url); }
  }

  async function jpeg(canvas) {
    const blob = await new Promise((resolve, reject) => canvas.toBlob(
      b => b ? resolve(b) : reject(Error('Could not save the engraving job sheet.')), 'image/jpeg', 0.94));
    return new Uint8Array(await blob.arrayBuffer());
  }

  // Small, dependency-free PDF writer: US Letter pages, with the original layout
  // JSON preserved as a PDF attachment instead of a long order-line property.
  function pdf(images, metadata) {
    const enc = new TextEncoder(), chunks = [], offsets = [0];
    let length = 0;
    const add = value => {
      const bytes = typeof value === 'string' ? enc.encode(value) : value;
      chunks.push(bytes); length += bytes.length;
    };
    const obj = (id, value) => { offsets[id] = length; add(`${id} 0 obj\n${value}\nendobj\n`); };
    const stream = (id, dictionary, bytes) => {
      offsets[id] = length; add(`${id} 0 obj\n<< ${dictionary} /Length ${bytes.length} >>\nstream\n`);
      add(bytes); add('\nendstream\nendobj\n');
    };
    const dataId = 3 + images.length * 3, fileId = dataId + 1;
    add('%PDF-1.7\n%StatenTools\n');
    obj(1, `<< /Type /Catalog /Pages 2 0 R /Names << /EmbeddedFiles << /Names [(engraving-layout.json) ${fileId} 0 R] >> >> >>`);
    obj(2, `<< /Type /Pages /Count ${images.length} /Kids [${images.map((_, i) => `${5 + i * 3} 0 R`).join(' ')}] >>`);
    images.forEach((bytes, i) => {
      const id = 3 + i * 3;
      stream(id, `/Type /XObject /Subtype /Image /Width ${W} /Height ${H} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode`, bytes);
      stream(id + 1, '', enc.encode('q\n612 0 0 792 0 0 cm\n/Preview Do\nQ'));
      obj(id + 2, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /XObject << /Preview ${id} 0 R >> >> /Contents ${id + 1} 0 R >>`);
    });
    stream(dataId, '/Type /EmbeddedFile /Subtype /application#2Fjson', enc.encode(JSON.stringify(metadata, null, 2)));
    obj(fileId, `<< /Type /Filespec /F (engraving-layout.json) /UF (engraving-layout.json) /Desc (Original engraving configuration and job reference) /EF << /F ${dataId} 0 R >> >>`);
    const xref = length;
    add(`xref\n0 ${fileId + 1}\n0000000000 65535 f \n`);
    for (let i = 1; i <= fileId; i++) add(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    add(`trailer\n<< /Size ${fileId + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
    return new Blob(chunks, {type: 'application/pdf'});
  }

  async function create({preview, reference, product, variantId, company, logo, originalName, notes, sides, fontLabel, price}) {
    const layout = JSON.parse(preview.settings);
    const locations = preview.locations;
    const placement = locations?.label || `${sides} side${Number(sides) === 1 ? '' : 's'} — placement to be confirmed before engraving`;
    const finish = 'Single-color black laser mark or paint-burn. No full-color printing.';
    const per = locations?.marksPerFace || 1;
    const faceText = per === 3 ? '3 marks at 0, 120 and 240 degrees' : '1 mark';
    const placementPages = [
      ...(preview.diagram ? [{title: `Package layout: ${locations?.packageName || 'engraving'} (${locations?.totalMarks || sides} marks per tool)`, file: preview.diagram,
        note: `${placement}. Diagram shows front face, back face${locations?.frameSides ? ' and frame' : ''}; the back face is drawn, not photographed. Not to scale.`}] : []),
      ...(preview.mockup ? [{title: 'Front wheel face placement (photo)', file: preview.mockup.file,
        note: `Front face: ${faceText}.${locations?.wheelSides === 2 ? ` Back face: repeat ${faceText} with the same artwork (see package layout page).` : ' Back face: no marks.'} Image shows illustrative placement, not a scaled manufacturing drawing.`}] : []),
      ...(preview.frameMockup ? [{title: 'Frame / arm placement', file: preview.frameMockup.file,
        note: 'One frame side only. Fit the artwork to the arm; confirm physical dimensions before engraving. Wheel dimensions do not apply to the frame.'}] : [])
    ];
    const total = 2 + placementPages.length;
    const first = page('Job instructions', reference, 1, total);
    let y = 193;
    y = row(first.ctx, 'Engrave on', product, y);
    y = row(first.ctx, 'Service', logo ? 'Customer logo' : 'Company name / text', y);
    y = row(first.ctx, logo ? 'Original file' : 'Exact text', logo ? originalName : company, y);
    y = row(first.ctx, 'Package', `${locations?.packageName || ''} \u2014 ${locations?.totalMarks || sides} mark(s) per tool${price ? `, ${price} per tool` : ''}`, y);
    y = row(first.ctx, 'Location', placement, y);
    if (!logo && fontLabel) y = row(first.ctx, 'Lettering font', fontLabel, y);
    y = row(first.ctx, 'Size', preview.size, y);
    y = row(first.ctx, 'Finish', finish, y);
    y = row(first.ctx, 'Customer notes', notes || 'None supplied', y);
    y = row(first.ctx, 'Approval & policy', 'Customer confirmed text / artwork, font, package and single-color finish. Engraved items: no returns or exchanges; covered under warranty with new engraving on replacements.', y);
    y = row(first.ctx, 'Quantity', 'Use the engraving quantity on the final Shopify order. Every listed mark applies to each linked tool.', y);
    if (preview.sheaveSize) y = row(first.ctx, 'Handling', '1–2 extra handling days per sheave, in addition to normal processing and shipping.', y);
    // Product IDs remain available to production without cluttering the order summary.
    text(first.ctx, `Tool variant reference: ${variantId}`, M, y + 6, W - M * 2, 18);
    if (y > H - 190) throw Error('The job instructions are too long. Shorten the placement notes and try again.');
    const images = [await jpeg(first.canvas)];
    const art = page('Approved artwork preview', reference, 2, total);
    let artY = text(art.ctx, logo ? `Original artwork: ${originalName}` : `Exact text: ${company}`, M, 196, W - M * 2, 28, true);
    artY = text(art.ctx, preview.size, M, artY + 24, W - M * 2, 24);
    const box = {x: M + 20, y: artY + 70, w: W - M * 2 - 40, h: H - artY - 400};
    art.ctx.strokeStyle = '#dfe5df'; art.ctx.strokeRect(box.x - 20, box.y - 20, box.w + 40, box.h + 40);
    await image(art.ctx, preview.file, box);
    text(art.ctx, 'Enlarged for review — not actual size. Use the stated dimensions and the original artwork file for production, not a screenshot of this page.', M, H - 270, W - M * 2, 24);
    images.push(await jpeg(art.canvas));
    for (const [i, item] of placementPages.entries()) {
      const p = page(item.title, reference, i + 3, total);
      const top = text(p.ctx, item.note, M, 193, W - M * 2, 26);
      await image(p.ctx, item.file, {x: M, y: top + 35, w: W - M * 2, h: H - top - 200});
      images.push(await jpeg(p.canvas));
    }
    const blob = pdf(images, {reference, product, variantId, package: locations?.package || null, totalMarks: locations?.totalMarks || null, font: logo ? null : fontLabel || null, company: logo ? null : company, originalName: originalName || null, notes, layout});
    return new File([blob], `${reference}-job-sheet.pdf`, {type: 'application/pdf'});
  }
  window.stEngravingJobSheet = {create};
})();
